import React, { useState, useEffect } from 'react';
import {
  FaFlask,
  FaTint,
  FaBurn,
  FaWineBottle,
  FaDownload,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const produtos = [
  {
    id: 'etanol-hidratado',
    nome: 'Etanol Hidratado',
    icone: <FaTint className="text-green-600 text-2xl mr-2" />,
    imagem: '/images/etanol-hidratado.jpg',
    descricao:
      'Com alto teor alcoólico entre 95,1% e 96,5%, o Etanol Hidratado é amplamente utilizado como biocombustível em veículos flex. É um produto incolor, com pH controlado e pureza garantida conforme normas técnicas da ANP.',
  },
  {
    id: 'etanol-anidro',
    nome: 'Etanol Anidro',
    icone: <FaBurn className="text-green-600 text-2xl mr-2" />,
    imagem: '/images/etanol-anidro.jpg',
    descricao:
      'Destinado à mistura com gasolina, o Etanol Anidro possui pureza superior a 99,3%. Garante eficiência energética e reduz emissões, sendo essencial para a matriz energética sustentável do Brasil.',
  },
  {
    id: 'etanol-neutro',
    nome: 'Etanol Neutro',
    icone: <FaFlask className="text-green-600 text-2xl mr-2" />,
    imagem: '/images/etanol-neutro.jpg',
    descricao:
      'Indicado para indústrias cosméticas, farmacêuticas e alimentícias, o Etanol Neutro tem grau alcoólico acima de 96,1% e odor característico. É reconhecido por sua elevada pureza e baixa acidez.',
  },
  {
    id: 'cachaca',
    nome: 'Cachaça',
    icone: <FaWineBottle className="text-green-600 text-2xl mr-2" />,
    imagem: '/images/cachaca.jpg',
    descricao:
      'Produzida com excelência, a Cachaça da Usina Rosa possui graduação alcoólica entre 38% e 48%. Límpida, transparente e com sabor marcante, atende aos mais altos padrões do MAPA.',
  },
];

export default function ProdutosSection() {
  const [ativoIndex, setAtivoIndex] = useState(0);
  const ativo = produtos[ativoIndex];

  const handlePrev = () => {
    setAtivoIndex((prev) => (prev === 0 ? produtos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setAtivoIndex((prev) => (prev === produtos.length - 1 ? 0 : prev + 1));
  };

  // Auto-play do carrossel no mobile
  useEffect(() => {
    const interval = setInterval(() => {
      setAtivoIndex((prev) => (prev === produtos.length - 1 ? 0 : prev + 1));
    }, 6000); // 6 segundos

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
      {/* Fundo borrado */}
      <div className="absolute inset-0">
        <img
          src="/images/bg-produtos.jpg"
          alt="Fundo Produtos"
          className="w-full h-full object-cover scale-110"
          style={{ filter: 'blur(2px)' }}
        />
        <div className="absolute inset-0 bg-white/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Título */}
        <div className="text-center mb-10 md:mb-14 px-1">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-5xl font-extrabold text-green-800 mb-4 tracking-tight uppercase"
          >
            Nossos Produtos
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-base md:text-xl text-gray-700 max-w-2xl mx-auto"
          >
            Produzimos com excelência e tecnologia os principais tipos de etanol e derivados da cana-de-açúcar, respeitando as normas técnicas e o compromisso com a sustentabilidade.
          </motion.p>
        </div>

        {/* ====== MOBILE (CARROSSEL AUTO + MANUAL, SEM ÍCONES) ====== */}
        <div className="lg:hidden">
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={ativo.id}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35 }}
                className="bg-white/85 border border-gray-200 shadow-xl rounded-2xl p-5 flex flex-col gap-4 items-center backdrop-blur-sm"
              >
                <img
                  src={ativo.imagem}
                  alt={ativo.nome}
                  className="w-full h-56 object-cover rounded-xl shadow-md"
                />
                <div className="text-center">
                  <h3 className="text-xl font-bold text-green-700 mb-2">
                    {ativo.nome}
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {ativo.descricao}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controles do carrossel (manual) */}
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md border border-gray-200 active:scale-95"
              aria-label="Produto anterior"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md border border-gray-200 active:scale-95"
              aria-label="Próximo produto"
            >
              <FaChevronRight />
            </button>

            {/* Bolinhas de indicador */}
            <div className="flex justify-center gap-2 mt-4">
              {produtos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setAtivoIndex(index)}
                  className={`h-2.5 w-2.5 rounded-full transition-all ${
                    index === ativoIndex
                      ? 'bg-green-500 w-5'
                      : 'bg-green-200 hover:bg-green-300'
                  }`}
                  aria-label={`Ir para o produto ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ====== DESKTOP / TABLET (LAYOUT COM TABS – MANTIDO) ====== */}
        <div className="hidden lg:flex flex-col lg:flex-row gap-8">
          {/* Tabs laterais */}
          <div className="bg-gray-100 rounded-xl p-6 lg:w-1/3 space-y-4">
            {produtos.map((produto, index) => (
              <button
                key={produto.id}
                onClick={() => setAtivoIndex(index)}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium text-lg flex items-center transition-all duration-300 hover:bg-green-100 ${
                  ativoIndex === index
                    ? 'bg-green-100 text-green-800 font-semibold shadow-sm'
                    : 'text-gray-700'
                }`}
              >
                {produto.icone}
                {produto.nome}
              </button>
            ))}
          </div>

          {/* Conteúdo com animação */}
          <div className="flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={ativo.id}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
                className="bg-white/80 border border-gray-200 shadow-xl rounded-xl p-8 flex flex-col lg:flex-row gap-8 items-center backdrop-blur-sm"
              >
                <img
                  src={ativo.imagem}
                  alt={ativo.nome}
                  className="w-full lg:w-1/2 h-72 object-cover rounded-lg shadow"
                />
                <div className="lg:w-1/2 text-center lg:text-left">
                  <h3 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
                    {ativo.icone}
                    {ativo.nome}
                  </h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {ativo.descricao}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Seção adicional: Precisa de mais detalhes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-12 md:mt-16 relative overflow-hidden rounded-2xl border border-green-200 shadow-lg bg-gradient-to-r from-green-50 via-white to-green-50"
        >
          <div className="relative z-10 p-6 md:p-10 flex flex-col items-center text-center gap-4 md:gap-6">
            <h4 className="text-xl md:text-3xl font-bold text-green-800">
              Precisa de mais detalhes técnicos?
            </h4>
            <p className="text-gray-700 text-sm md:text-lg max-w-2xl">
              Baixe nossa apresentação comercial e tenha acesso a informações técnicas completas sobre cada produto, incluindo especificações de pureza, controle de qualidade, processos de destilação, padrões da ANP e dados laboratoriais de desempenho. Conheça em detalhe o compromisso da Usina Rosa com inovação e excelência industrial.
            </p>
            <a
              href="/downloads/ROSA SA  APRESENTAÇÃO COMERCIAL.pdf"
              download="ROSA SA  APRESENTAÇÃO COMERCIAL.pdf"
              className="group inline-flex items-center gap-3 rounded-2xl bg-green-700 hover:bg-green-800 text-white font-semibold px-6 md:px-8 py-3 md:py-4 transition-colors shadow-md"
            >
              <FaDownload className="text-lg" />
              <span>Baixar agora</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
